import { useEffect, useState, useCallback, useMemo, useContext, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LoginContext } from "../../LoginContext";
import { getBaseUrl } from "../../config/apiConfig";
import { apiService } from "../../backend/api";
import { RefreshCw, TrendingUp, Users, BarChart3, CreditCard, Ticket, TrendingDown, Activity, Euro, ShieldCheck, Shield, Settings } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Tabs, TabsContent } from "../../components/ui/tabs";
import AdminPricing from "./AdminPricing";
import AdminDiscounts from "./AdminDiscounts";
import AdminScanSettings from "./AdminScanSettings";

// Sub-components
import OverviewTab from "./components/OverviewTab";
import UsersTab from "./components/UsersTab";
import AnalyticsTab from "./components/AnalyticsTab";
import UserDetailModal from "./components/UserDetailModal";

const AdminDashboard = () => {
  const { t } = useTranslation();
  const { loginInfo } = useContext(LoginContext);
  const currentUsername = loginInfo?.username;
  const [users, setUsers] = useState([]);
  const [error, setError] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab ] = useState("overview");
  const [summaryStats, setSummaryStats] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeJobs, setActiveJobs] = useState([]);
  const [infraStats, setInfraStats] = useState(null);
  const [revenueData, setRevenueData] = useState(null);
  const [timeframe, setTimeframe] = useState("last-30-days");
  const [isEditingRoles, setIsEditingRoles] = useState(false);
  const [tempSelectedRoles, setTempSelectedRoles] = useState([]);
  const [allSystemRoles, setAllSystemRoles] = useState([]);
  const [isSavingRoles, setIsSavingRoles] = useState(false);
  const navigate = useNavigate();
  const mainRef = useRef(null);

  // Reset scroll to top when tab changes
  useEffect(() => {
    if (mainRef.current) {
      mainRef.current.scrollTo(0, 0);
    }
  }, [activeTab]);

  // Mocked stats (would be fetched from a real endpoint in the future)
  const stats = useMemo(() => {
    if (!summaryStats) return [];

    return [
      {
        label: t("adminDashboard.stats.totalRevenue.label"),
        value: `€${(summaryStats.subscriptions.monthlyRecurringRevenue || 0).toFixed(2)}`,
        icon: Euro,
        growth: "+12.5%",
        trend: t("adminDashboard.stats.totalRevenue.trend"),
        subtext: t("adminDashboard.stats.totalRevenue.subtext")
      },
      {
        label: t("adminDashboard.stats.newCustomers.label"),
        value: summaryStats.users.newlyRegistered.lastMonth,
        icon: Users,
        growth: "-20%",
        isNegative: true,
        trend: t("adminDashboard.stats.newCustomers.trend"),
        subtext: t("adminDashboard.stats.newCustomers.subtext")
      },
      {
        label: t("adminDashboard.stats.activeAccounts.label"),
        value: summaryStats.subscriptions.totalActive,
        icon: ShieldCheck,
        growth: "+12.5%",
        trend: t("adminDashboard.stats.activeAccounts.trend"),
        subtext: t("adminDashboard.stats.activeAccounts.subtext")
      },
      {
        label: t("adminDashboard.stats.growthRate.label"),
        value: "4.5%",
        icon: Activity,
        growth: "+4.5%",
        trend: t("adminDashboard.stats.growthRate.trend"),
        subtext: t("adminDashboard.stats.growthRate.subtext")
      }
    ];
  }, [summaryStats, infraStats, activeJobs]);

  // Extract unique roles from users for the filter
  const availableRoles = useMemo(() => {
    const roles = new Set();
    users.forEach(user => {
      user.roles?.forEach(role => {
        const roleName = typeof role === 'string' ? role : role.name;
        if (roleName) roles.add(roleName);
      });
    });
    return Array.from(roles).sort();
  }, [users]);

  const recentActivities = useMemo(() => {
    const allScans = users.flatMap(user =>
      (user.scans || []).map(scan => ({
        ...scan,
        username: user.username,
        email: user.email
      }))
    );
    return allScans.sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 6);
  }, [users]);

  // Aggregate scan data into time-series based on timeframe
  const dynamicChartData = useMemo(() => {
    if (!users || users.length === 0) return [];

    const allScans = users.flatMap(user => (user.scans || []));
    const now = new Date();
    let daysToLookBack = 30;
    if (timeframe === "last-7-days") daysToLookBack = 7;
    if (timeframe === "last-3-months") daysToLookBack = 90;

    const startDate = new Date();
    startDate.setDate(now.getDate() - daysToLookBack);

    // Filter scans within range
    const filteredScans = allScans.filter(scan => new Date(scan.date) >= startDate);

    // Group by date
    const dailyCounts = {};
    for (let i = 0; i < daysToLookBack; i++) {
        const d = new Date(startDate);
        d.setDate(startDate.getDate() + i);
        const dateKey = d.toISOString().split('T')[0];
        dailyCounts[dateKey] = 0;
    }

    filteredScans.forEach(scan => {
        const dateKey = new Date(scan.date).toISOString().split('T')[0];
        if (dailyCounts.hasOwnProperty(dateKey)) {
            dailyCounts[dateKey]++;
        }
    });

    return Object.entries(dailyCounts).map(([date, count]) => ({ date, count }));
  }, [users, timeframe]);

  const handleUserSelect = (selectedUsername) => {
    navigate("/projects", {
      state: { username: selectedUsername },
    });
  };

  const fetchUsersAndCheckAdmin = useCallback(async () => {
    if (!currentUsername) {
      setError(t("adminDashboard.errorNoUsername"));
      setLoading(false);
      return;
    }

    try {
      // console.log("AdminDashboard: fetchUsersAndCheckAdmin started");
      setLoading(true);
      setError(null);

      // --- 1. IDENTITY CHECK (Priority) ---
      let userData = [];
      try {
        // console.log("AdminDashboard: Fetching user details (with 10s timeout)...");

        userData = await apiService.request(`${getBaseUrl()}/api/statistics/users-details`);

        // console.log("AdminDashboard: User details fetched, count:", userData?.length);
        if (userData && Array.isArray(userData)) {
          setUsers(userData);
        }
      } catch (e) {
        console.error("AdminDashboard: Identity fetch failed or timed out:", e);
      }

      // Evaluate admin status
      const hasAccessToStats = !!(userData && Array.isArray(userData) && userData.length > 0);

      const userObj = loginInfo?.user;
      const userRoles = userObj?.roles || [];
      const hasAdminRoleInStorage = userRoles.some(r =>
        (typeof r === 'string' && r === 'admin') ||
        (r && typeof r === 'object' && r.name === 'admin')
      );

      const hasAdminToken = false; // Legacy fallback removed, using memory state instead

      const currentUser = userData?.find((u) => u.username === currentUsername);
      const hasAdminInDB = currentUser?.roles?.some((r) =>
        (typeof r === 'string' && r === 'admin') || (r.name === "admin")
      );

      // sysadmin is always an admin if they have any local proof
      const isSysAdmin = currentUsername === 'sysadmin';

      const determinedAdmin = hasAccessToStats || hasAdminRoleInStorage || hasAdminToken || hasAdminInDB || isSysAdmin;
      setIsAdmin(determinedAdmin);

      // --- 2. DATA FETCHING (Non-blocking) ---
      if (determinedAdmin) {
        apiService.request(`${getBaseUrl()}/api/statistics/summary`)
          .then(setSummaryStats)
          .catch(e => console.error("Summary fetch failed:", e.message));

        apiService.request(`${getBaseUrl()}/api/statistics/active-jobs`)
          .then(setActiveJobs)
          .catch(e => console.error("Jobs fetch failed:", e.message));

        apiService.request(`${getBaseUrl()}/api/statistics/infrastructure`)
          .then(setInfraStats)
          .catch(e => console.error("Infra fetch failed:", e.message));

        apiService.request(`${getBaseUrl()}/api/statistics/revenue-analysis`)
          .then(setRevenueData)
          .catch(e => console.error("Revenue fetch failed:", e.message));
      }
    } catch (err) {
      console.error("AdminDashboard: Dashboard initialization error:", err);
    } finally {
      // console.log("AdminDashboard: fetchUsersAndCheckAdmin finally - setting loading false");
      setLoading(false);
    }
  }, [currentUsername, loginInfo]);

  const fetchAvailableRoles = useCallback(async () => {
    try {
      const data = await apiService.request(`${getBaseUrl()}/api/user/roles`);
      if (data && Array.isArray(data)) {
        setAllSystemRoles(data);
      }
    } catch (err) {
      console.error("Fehler beim Abrufen der verfügbaren Rollen:", err);
    }
  }, []);

  useEffect(() => {
    fetchUsersAndCheckAdmin();
    fetchAvailableRoles();
  }, [fetchUsersAndCheckAdmin, fetchAvailableRoles]);



  const handleAssignRole = (user) => {
    setSelectedUser(user);
    // Only take the first role if multiple exist (legacy)
    const existingRoles = user.roles ? user.roles.map(r => typeof r === 'string' ? r : r.name) : [];
    setTempSelectedRoles(existingRoles.length > 0 ? [existingRoles[0]] : []);
    setIsEditingRoles(true);
    setIsModalOpen(true);
  };

  const handleRoleToggle = (roleName) => {
    // Only allow one role at a time
    setTempSelectedRoles([roleName]);
  };

  const handleSaveRoles = async () => {
    if (!selectedUser) return;
    setIsSavingRoles(true);
    try {
      const data = await apiService.request(`${getBaseUrl()}/api/user/assign-roles`, {
        method: "POST",
        body: JSON.stringify({
          userId: selectedUser._id,
          roles: tempSelectedRoles,
        }),
      });

      if (data && data.status === "success") {
        setIsEditingRoles(false);
        // Refresh users to show updated roles
        fetchUsersAndCheckAdmin();
      }
    } catch (err) {
      console.error("Fehler beim Speichern der Rollen:", err);
      setError(t("adminDashboard.errorSaveRoles"));
    } finally {
      setIsSavingRoles(false);
    }
  };

  const handleUpdateStatus = async (userId, newStatus) => {
    try {
      setLoading(true);
      await apiService.request(`${getBaseUrl()}/api/user/status`, {
        method: 'POST',
        body: JSON.stringify({ userId, status: newStatus })
      });
      // Refresh user list
      await fetchUsersAndCheckAdmin();
    } catch (err) {
      console.error("Error updating user status:", err);
      setError(t("adminDashboard.errorUpdateStatus"));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex flex-col items-center justify-center p-4 transition-colors duration-300">
        <RefreshCw className="text-[#0f7699] dark:text-[#4fc3f7] animate-spin mb-4" size={48} />
        <p className="text-slate-500 dark:text-slate-400 font-normal">{t("adminDashboard.loadingDashboard")}</p>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex flex-col items-center justify-center p-4 text-center transition-colors duration-300">
        <div className="w-20 h-20 bg-red-50 dark:bg-red-900/30 text-red-500 dark:text-red-400 rounded-full flex items-center justify-center mb-6">
          <Shield size={40} />
        </div>
        <h2 className="text-2xl font-medium text-slate-800 dark:text-white mb-2">{t("adminDashboard.accessDenied")}</h2>
        <p className="text-slate-500 dark:text-slate-400 max-w-sm">{t("adminDashboard.accessDeniedDesc")}</p>
        <button
          onClick={() => navigate("/")}
          className="mt-8 px-6 py-3 bg-slate-800 dark:bg-slate-700 text-white rounded-xl font-medium hover:bg-slate-700 dark:hover:bg-slate-600 transition-all"
        >
          {t("adminDashboard.backToDashboard")}
        </button>
      </div>
    );
  }

  return (
    <div className="flex min-h-[calc(100vh-80px)] bg-background font-sans text-foreground">
      {/* SIDEBAR */}
      <aside className="hidden md:flex w-64 flex-col border-r border-border bg-white dark:bg-slate-800 sticky top-20 h-[calc(100vh-80px)]">
        <div className="p-6">
          <div className="flex items-center space-x-2 mb-8">
            <div className="w-8 h-8 rounded-full border-2 border-primary flex items-center justify-center">
              <div className="w-3 h-3 rounded-full bg-primary" />
            </div>
            <span className="font-bold text-lg tracking-tight">Admin Dashboard</span>
          </div>

          <nav className="space-y-1">
            <p className="px-3 text-[10px] font-bold text-muted-foreground uppercase tracking-widest mb-4">{t("adminDashboard.mainMenu")}</p>
            <button
              onClick={() => setActiveTab("overview")}
              className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === "overview" ? "bg-muted text-foreground" : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
              }`}
            >
              <TrendingUp size={18} />
              <span>{t("adminDashboard.tabs.dashboard")}</span>
            </button>
            <button
              onClick={() => setActiveTab("users")}
              className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === "users" ? "bg-muted text-foreground" : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
              }`}
            >
              <Users size={18} />
              <span>{t("adminDashboard.tabs.users")}</span>
            </button>
            <button
              onClick={() => setActiveTab("analytics")}
              className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === "analytics" ? "bg-muted text-foreground" : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
              }`}
            >
              <BarChart3 size={18} />
              <span>Analytics</span>
            </button>
          </nav>

          <nav className="mt-8 space-y-1">
            <p className="px-3 text-[10px] font-bold text-muted-foreground uppercase tracking-widest mb-4">{t("adminDashboard.management")}</p>
            <button
              onClick={() => setActiveTab("pricing")}
              className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === "pricing" ? "bg-muted text-foreground" : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
              }`}
            >
              <CreditCard size={18} />
              <span>{t("adminDashboard.tabs.pricing")}</span>
            </button>
            <button
              onClick={() => setActiveTab("discounts")}
              className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === "discounts" ? "bg-muted text-foreground" : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
              }`}
            >
              <Ticket size={18} />
              <span>{t("adminDashboard.tabs.discounts")}</span>
            </button>
            <button
              onClick={() => setActiveTab("settings")}
              className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeTab === "settings" ? "bg-muted text-foreground" : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
              }`}
            >
              <Settings size={18} />
              <span>{t("adminDashboard.tabs.settings")}</span>
            </button>
          </nav>
        </div>


      </aside>

      {/* MOBILE NAV (Bottom) */}
      <div className="md:hidden fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-white/80 dark:bg-slate-800/80 backdrop-blur-md rounded-full border border-border shadow-xl px-3 py-2 flex items-center space-x-3 sm:space-x-6">
        <button onClick={() => setActiveTab("overview")} className={`w-11 h-11 flex items-center justify-center rounded-xl transition-colors ${activeTab === "overview" ? "text-primary bg-primary/10" : "text-muted-foreground"}`}><TrendingUp size={20} /></button>
        <button onClick={() => setActiveTab("users")} className={`w-11 h-11 flex items-center justify-center rounded-xl transition-colors ${activeTab === "users" ? "text-primary bg-primary/10" : "text-muted-foreground"}`}><Users size={20} /></button>
        <button onClick={() => setActiveTab("analytics")} className={`w-11 h-11 flex items-center justify-center rounded-xl transition-colors ${activeTab === "analytics" ? "text-primary bg-primary/10" : "text-muted-foreground"}`}><BarChart3 size={20} /></button>
        <button onClick={() => setActiveTab("pricing")} className={`w-11 h-11 flex items-center justify-center rounded-xl transition-colors ${activeTab === "pricing" ? "text-primary bg-primary/10" : "text-muted-foreground"}`}><CreditCard size={20} /></button>
        <button onClick={() => setActiveTab("discounts")} className={`w-11 h-11 flex items-center justify-center rounded-xl transition-colors ${activeTab === "discounts" ? "text-primary bg-primary/10" : "text-muted-foreground"}`}><Ticket size={20} /></button>
        <button onClick={() => setActiveTab("settings")} className={`w-11 h-11 flex items-center justify-center rounded-xl transition-colors ${activeTab === "settings" ? "text-primary bg-primary/10" : "text-muted-foreground"}`}><Settings size={20} /></button>
      </div>

      {/* MAIN CONTENT AREA */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 border-b border-border bg-white dark:bg-slate-800 flex items-center justify-between px-4 md:px-8 sticky top-20 z-40 transition-colors duration-300">
          <h2 className="text-sm font-bold uppercase tracking-widest text-muted-foreground">
            {activeTab === 'overview' ? t("adminDashboard.tabs.dashboard") :
             activeTab === 'users' ? t("adminDashboard.tabs.users") :
             activeTab === 'analytics' ? t("adminDashboard.tabs.analytics") :
             activeTab === 'pricing' ? t("adminDashboard.tabs.pricing") :
             activeTab === 'settings' ? t("adminDashboard.tabs.settings") : t("adminDashboard.tabs.discounts")}
          </h2>
          <div className="flex items-center space-x-4">
            <Button
              variant="outline"
              size="sm"
              onClick={fetchUsersAndCheckAdmin}
              className="h-8 rounded-lg text-xs font-semibold"
            >
              <RefreshCw size={14} className={`mr-2 ${loading ? "animate-spin" : ""}`} />
              Sync
            </Button>

          </div>
        </header>

        <main ref={mainRef} className="flex-1 p-4 md:p-8 pb-24 md:pb-8 overflow-y-auto animate-in fade-in slide-in-from-bottom-4 duration-500">
          <div className="max-w-6xl mx-auto space-y-8">
            <Tabs value={activeTab} className="w-full space-y-8 outline-none">

              <OverviewTab
                stats={stats}
                dynamicChartData={dynamicChartData}
                timeframe={timeframe}
                setTimeframe={setTimeframe}
                activeJobs={activeJobs}
                recentActivities={recentActivities}
              />

              <AnalyticsTab revenueData={revenueData} />

              <UsersTab
                users={users}
                allSystemRoles={allSystemRoles}
                error={error}
                setSelectedUser={setSelectedUser}
                setIsModalOpen={setIsModalOpen}
                handleUpdateStatus={handleUpdateStatus}
                handleAssignRole={handleAssignRole}
              />

              <TabsContent value="pricing" className="outline-none">
                <AdminPricing />
              </TabsContent>

              <TabsContent value="discounts" className="outline-none">
                <AdminDiscounts />
              </TabsContent>

              <TabsContent value="settings" className="outline-none">
                <AdminScanSettings />
              </TabsContent>

            </Tabs>
          </div>
        </main>
      </div>

      <UserDetailModal
        isOpen={isModalOpen}
        onOpenChange={(val) => {
          setIsModalOpen(val);
          if (!val) setIsEditingRoles(false);
        }}
        selectedUser={selectedUser}
        isEditingRoles={isEditingRoles}
        setIsEditingRoles={setIsEditingRoles}
        allSystemRoles={allSystemRoles}
        tempSelectedRoles={tempSelectedRoles}
        handleRoleToggle={handleRoleToggle}
        handleSaveRoles={handleSaveRoles}
        isSavingRoles={isSavingRoles}
        handleUserSelect={handleUserSelect}
      />
    </div>
  );
};

export default AdminDashboard;
